import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { canAccessRoute, getRoleDashboard, getRoleName } from '../utils/rolePermissions';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Logout Function - Memoized
  const logout = useCallback(async (isAutoLogout = false, isSilent = false) => {
    try {
      // Best effort API logout
      if (!isSilent) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      console.error('Logout logging failed:', error);
    } finally {
      // Clear local storage and state regardless of API call success
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      setUser(null);
      if (isAutoLogout) {
        toast.error('Session expired due to inactivity');
      } else if (!isSilent) {
        toast.success('Logged out successfully');
      }
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      // Validate session with backend (sends cookie automatically)
      const { data } = await api.get('/auth/me');
      if (data.success) {
        setUser(data.data);
        // Cache user details for non-security-critical offline UI display (use sessionStorage for testing)
        sessionStorage.setItem('user', JSON.stringify(data.data));
      } else {
        logout(false, true); // Silent logout
      }
    } catch (error) {
      // Always treat any auth failure or cookie validation error as logged out
      logout(false, true); // Silent logout
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    // One-time migration: Clear old persistent tokens (security update)
    const migrationKey = 'auth_migration_v2_completed';
    if (!localStorage.getItem(migrationKey)) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.setItem(migrationKey, 'true');
      console.log('Security update: Cleared old persistent tokens. Please log in again.');
    }
    checkAuth();
  }, [checkAuth]);

  // Auto Logout Logic (30 Minutes Inactivity)
  useEffect(() => {
    if (!user) return;

    const TIMEOUT_MS = 30 * 60 * 1000; // 30 Minutes
    const KEEP_ALIVE_MS = 4 * 60 * 1000; // 4 Minutes - refresh token before expiry
    let activityTimer;
    let keepAliveTimer;

    const resetTimer = () => {
      clearTimeout(activityTimer);
      activityTimer = setTimeout(() => {
        logout(true); // True indicates auto-logout
      }, TIMEOUT_MS);
    };

    // Throttle the event listeners (run at most once per second)
    let lastExec = 0;
    const handleUserActivity = () => {
      const now = Date.now();
      if (now - lastExec > 1000) {
        resetTimer();
        lastExec = now;
      }
    };

    // Keep-alive mechanism to prevent connection timeouts
    const keepAlive = async () => {
      try {
        // Make a lightweight request to keep session alive
        await api.get('/auth/me');
      } catch (error) {
        console.error('Keep-alive failed:', error);
      }
    };

    // Initialize timers
    resetTimer();
    keepAliveTimer = setInterval(keepAlive, KEEP_ALIVE_MS);

    // Listen for events
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keypress', handleUserActivity);
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);
    window.addEventListener('touchstart', handleUserActivity);

    return () => {
      clearTimeout(activityTimer);
      clearInterval(keepAliveTimer);
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keypress', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
    };
  }, [user, logout]);

  // Clear session on window close/navigation - REMOVED: Managed by browser for sessionStorage
  /* 
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clear session storage on tab close or navigation away
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  */

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password });

      if (data.success) {
        if (data.requireOtp) {
          return { success: true, requireOtp: true, email: data.email, message: data.message };
        }
        // Fallback for non-2FA (if ever needed)
        // ... (Currently backend forces 2FA so this path might be unused but good for safety)
        if (data.data) {
          return finalizeLogin(data.data, true);
        }
      }
      return { success: false, message: 'Unexpected response' };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      // toast.error(message); // Let caller handle UI for 2FA flow flexibility
      return { success: false, message };
    }
  };

  const verifyOtp = async (email, otp, rememberMe = true) => {
    try {
      const { data } = await api.post('/auth/verify-login', { email, otp });
      if (data.success) {
        return finalizeLogin(data.data, rememberMe);
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Verification failed';
      return { success: false, message };
    }
  };

  const finalizeLogin = (userData, rememberMe) => {
    // ALWAYS use sessionStorage for testing so tabs have separate users
    if (userData.token) {
      sessionStorage.setItem('token', userData.token);
      delete userData.token;
    }
    
    sessionStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    toast.success(`Welcome ${userData.fullName}!`);

    // Redirect students to external portal
    if (userData.role === 'student') {
      const studentPortalUrl = process.env.REACT_APP_STUDENT_PORTAL_URL || 'http://localhost:3000/student/dashboard';
      setTimeout(() => {
        window.location.href = studentPortalUrl;
      }, 1000);
      return { success: true, user: userData, redirect: studentPortalUrl };
    }

    return { success: true, user: userData };
  };

  const register = async (userData) => {
    try {
      const { data } = await api.post('/auth/register', userData);

      if (data.success) {
        toast.success('Registration successful! You can now login with your credentials.');
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const updateUser = (updatedData) => {
    const updatedUser = { ...user, ...updatedData };
    setUser(updatedUser);
    sessionStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const value = {
    user,
    loading,
    login,
    verifyOtp,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isCollegeAdmin: user?.role === 'college_admin',
    isStudent: user?.role === 'student',
    canAccessRoute: (route) => canAccessRoute(user?.role, route),
    getRoleDashboard: () => getRoleDashboard(user?.role),
    getRoleName: () => getRoleName(user?.role),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
